/**
 * The navigation as data: three trees (design 10.2): the portfolio (the same in both modes), a client in Work
 * mode, and a client in Expert mode. The rail renders these, the header takes its title from them, and the
 * portfolio's "Where everything is" index is generated from them, so adding a screen is adding a row.
 *
 * PROVENANCE: the shape of C:\alexander\apps\web\src\components\layout\nav-config.ts @ 0e341f4 (each item's label,
 * the question it answers, sections with headings; a counter that shows no badge when it is unknown, never 0).
 * CHANGES: labels are i18n keys; every item names the phase that fills it, and an item whose phase has not come
 * yet opens a page that says so rather than showing anything invented; items can be limited to firm roles.
 */

import type { MessageKey } from '../i18n/en'

export type Phase = 'O0' | 'O1' | 'O2' | 'O2b' | 'O3a' | 'O3b' | 'O4' | 'O5' | 'O6' | 'O8' | 'IP1' | 'IP6' | 'IP7' | 'IP9a' | 'IP9b' | 'IP10a' | 'IP11'
export type FirmRole = 'firm_admin' | 'regulatory_desk' | 'partner' | 'consultant' | 'auditor'

export interface NavItem {
  id: string
  /** The route; `:client` is the client segment. */
  to: string
  labelKey: MessageKey
  answersKey: MessageKey
  /** The phase that gives the screen its content. Before it, the screen says which phase brings it. */
  phase: Phase
  /** Shown only to people holding one of these firm roles. */
  firmRoles?: FirmRole[]
  /** A live counter (design 10.1): shown only when known and above zero, never as a guessed 0. */
  counter?: Counter
}

/** The counters the rail can show; each is read from one API answer (Shell's useNavCounts). */
export type Counter = 'needsYou' | 'actionsOpen' | 'cycleOverdue'

export interface NavSection {
  id: string
  labelKey: MessageKey
  items: readonly NavItem[]
}

export const BUILT: readonly Phase[] = ['O0', 'O1', 'IP1', 'IP6', 'IP7', 'IP9a', 'IP9b', 'IP10a']

export const PORTFOLIO_NAV: readonly NavSection[] = [
  {
    id: 'waiting',
    labelKey: 'nav.section.waiting',
    items: [
      { id: 'my-work', to: '/portfolio/work', labelKey: 'nav.myWork', answersKey: 'nav.myWork.answers', phase: 'O1', counter: 'needsYou' },
      { id: 'inbox', to: '/portfolio/inbox', labelKey: 'nav.inbox', answersKey: 'nav.inbox.answers', phase: 'IP6' },
    ],
  },
  {
    id: 'clients',
    labelKey: 'nav.section.clients',
    items: [
      { id: 'portfolio', to: '/portfolio', labelKey: 'nav.portfolio', answersKey: 'nav.portfolio.answers', phase: 'O0' },
      { id: 'portfolio-heat', to: '/portfolio/heat', labelKey: 'nav.portfolioHeat', answersKey: 'nav.portfolioHeat.answers', phase: 'IP7', firmRoles: ['partner'] },
      { id: 'cycle-all', to: '/portfolio/cycle', labelKey: 'nav.cycleAll', answersKey: 'nav.cycleAll.answers', phase: 'O1' },
      { id: 'changes-all', to: '/portfolio/changes', labelKey: 'nav.changesAll', answersKey: 'nav.changesAll.answers', phase: 'O3a' },
      { id: 'deliverables-all', to: '/portfolio/deliverables', labelKey: 'nav.deliverablesAll', answersKey: 'nav.deliverablesAll.answers', phase: 'O2' },
    ],
  },
  {
    id: 'firm',
    labelKey: 'nav.section.firm',
    items: [
      { id: 'people', to: '/firm/people', labelKey: 'nav.people', answersKey: 'nav.people.answers', phase: 'O0', firmRoles: ['firm_admin', 'auditor'] },
      { id: 'clients', to: '/firm/clients', labelKey: 'nav.clients', answersKey: 'nav.clients.answers', phase: 'O0', firmRoles: ['firm_admin', 'auditor', 'partner'] },
      { id: 'library', to: '/firm/library', labelKey: 'nav.library', answersKey: 'nav.library.answers', phase: 'O1' },
      { id: 'settings', to: '/firm/settings', labelKey: 'nav.settings', answersKey: 'nav.settings.answers', phase: 'O0' },
      { id: 'spend', to: '/firm/ai-spend', labelKey: 'nav.spend', answersKey: 'nav.spend.answers', phase: 'IP9b' },
      { id: 'about', to: '/about', labelKey: 'nav.about', answersKey: 'nav.about.answers', phase: 'O0' },
      { id: 'method', to: '/about/method', labelKey: 'nav.method', answersKey: 'nav.method.answers', phase: 'O0' },
    ],
  },
]

export const CLIENT_WORK_NAV: readonly NavSection[] = [
  {
    id: 'work',
    labelKey: 'nav.section.client',
    items: [
      { id: 'week', to: '/c/:client/work', labelKey: 'nav.work.week', answersKey: 'nav.work.week.answers', phase: 'O0' },
      { id: 'happened', to: '/c/:client/work/happened', labelKey: 'nav.work.happened', answersKey: 'nav.work.happened.answers', phase: 'O1' },
      { id: 'for-you', to: '/c/:client/work/for-you', labelKey: 'nav.work.forYou', answersKey: 'nav.work.forYou.answers', phase: 'O3b' },
      { id: 'for-client', to: '/c/:client/work/for-client', labelKey: 'nav.work.forClient', answersKey: 'nav.work.forClient.answers', phase: 'O3a' },
      { id: 'compared', to: '/c/:client/work/compared', labelKey: 'nav.work.compared', answersKey: 'nav.work.compared.answers', phase: 'O3a' },
      { id: 'deadlines', to: '/c/:client/work/deadlines', labelKey: 'nav.work.deadlines', answersKey: 'nav.work.deadlines.answers', phase: 'O1' },
      { id: 'todo', to: '/c/:client/work/todo', labelKey: 'nav.work.todo', answersKey: 'nav.work.todo.answers', phase: 'O1' },
    ],
  },
]

export const CLIENT_EXPERT_NAV: readonly NavSection[] = [
  {
    id: 'client',
    labelKey: 'nav.section.client',
    items: [
      { id: 'overview', to: '/c/:client', labelKey: 'nav.overview', answersKey: 'nav.overview.answers', phase: 'O0' },
      { id: 'services', to: '/c/:client/services', labelKey: 'nav.services', answersKey: 'nav.services.answers', phase: 'O1' },
      { id: 'mandate', to: '/c/:client/mandate', labelKey: 'nav.mandate', answersKey: 'nav.mandate.answers', phase: 'O1' },
      { id: 'business', to: '/c/:client/business', labelKey: 'nav.business', answersKey: 'nav.business.answers', phase: 'O1' },
      { id: 'cycle', to: '/c/:client/cycle', labelKey: 'nav.cycle', answersKey: 'nav.cycle.answers', phase: 'O1', counter: 'cycleOverdue' },
      { id: 'discover', to: '/c/:client/discover', labelKey: 'nav.discover', answersKey: 'nav.discover.answers', phase: 'IP1' },
      { id: 'current-state', to: '/c/:client/assessments', labelKey: 'nav.currentState', answersKey: 'nav.currentState.answers', phase: 'O1' },
      { id: 'progress', to: '/c/:client/progress', labelKey: 'nav.progress', answersKey: 'nav.progress.answers', phase: 'IP7' },
      { id: 'modules', to: '/c/:client/modules', labelKey: 'nav.modules', answersKey: 'nav.modules.answers', phase: 'IP10a' },
      { id: 'changes', to: '/c/:client/changes', labelKey: 'nav.changes', answersKey: 'nav.changes.answers', phase: 'O3a' },
    ],
  },
  {
    id: 'registers',
    labelKey: 'nav.section.registers',
    items: [
      { id: 'actions', to: '/c/:client/registers/actions', labelKey: 'nav.actions', answersKey: 'nav.actions.answers', phase: 'O1', counter: 'actionsOpen' },
      { id: 'more-registers', to: '/c/:client/registers', labelKey: 'nav.moreRegisters', answersKey: 'nav.moreRegisters.answers', phase: 'O2' },
    ],
  },
  {
    id: 'records',
    labelKey: 'nav.section.records',
    items: [
      { id: 'risk', to: '/c/:client/risk', labelKey: 'nav.risk', answersKey: 'nav.risk.answers', phase: 'O5' },
      { id: 'data', to: '/c/:client/data', labelKey: 'nav.data', answersKey: 'nav.data.answers', phase: 'O6' },
      { id: 'documents', to: '/c/:client/documents', labelKey: 'nav.documents', answersKey: 'nav.documents.answers', phase: 'O1' },
      { id: 'deliverables', to: '/c/:client/deliverables', labelKey: 'nav.deliverables', answersKey: 'nav.deliverables.answers', phase: 'O2' },
      { id: 'ai-drafts', to: '/c/:client/ai', labelKey: 'nav.aiDrafts', answersKey: 'nav.aiDrafts.answers', phase: 'IP9a' },
      { id: 'handoffs', to: '/c/:client/handoffs', labelKey: 'nav.handoffs', answersKey: 'nav.handoffs.answers', phase: 'IP11' },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.section.settings',
    items: [
      { id: 'profile', to: '/c/:client/profile', labelKey: 'nav.profile', answersKey: 'nav.profile.answers', phase: 'O1' },
      { id: 'members', to: '/c/:client/members', labelKey: 'nav.members', answersKey: 'nav.members.answers', phase: 'O0' },
      { id: 'customer-data', to: '/c/:client/customer-data', labelKey: 'nav.customerData', answersKey: 'nav.customerData.answers', phase: 'O1' },
      { id: 'audit', to: '/c/:client/audit', labelKey: 'nav.audit', answersKey: 'nav.audit.answers', phase: 'O0' },
    ],
  },
]

export const ALL_NAV: readonly NavSection[] = [...PORTFOLIO_NAV, ...CLIENT_WORK_NAV, ...CLIENT_EXPERT_NAV]

export function allItems(): NavItem[] {
  return ALL_NAV.flatMap((s) => s.items)
}

/** The item's address for a client. */
export function hrefFor(item: NavItem, client: string | null): string {
  return client ? item.to.replace(':client', client) : item.to
}

export function isBuilt(item: NavItem): boolean {
  return BUILT.includes(item.phase)
}

/** Whether a person with these firm roles sees the item. */
export function visibleTo(item: NavItem, roles: readonly string[]): boolean {
  return !item.firmRoles || item.firmRoles.some((r) => roles.includes(r))
}
